import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useCoreDispatch } from '../../store/CoreStore';
import { useBetSlice } from '../../store/slices/bets/selectors';
import { betActions } from '../../store/slices/bets/slice';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { useIsLargerThanWide } from '../../styles/hooks/media';
import { ChipConnected } from '../game/chip/ChipConnected';

import styles from './DraggableChip.module.css';

export const DraggableChipConnected: FC = () => {
  const dispatch = useCoreDispatch();
  const dragBuffer = useBetSlice.getPreliminaryDragBuffer();
  const isDragging = useBetSlice.getIsDragging();
  const [positionShift, setPositionShift] = useState({ x: 0, y: 0 });
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const isWide = useIsLargerThanWide();

  const handlePointerMove = useCallback(
    (event: PointerEvent): void => {
      if (dragBuffer) {
        const diffX = Math.round(event.clientX - dragBuffer.elementData.origin.x);
        const diffY = Math.round(event.clientY - dragBuffer.elementData.origin.y);

        // Check for minimum 5px shift in any direction before promoting drag buffer
        if (Math.abs(diffX) >= 5 || Math.abs(diffY) >= 5) {
          if (dragBuffer.preliminary) {
            dispatch(betActions.promoteDragBuffer());
          }
        }

        setPositionShift({
          x: diffX,
          y: diffY,
        });
      }
    },
    [dispatch, dragBuffer]
  );

  useEffect(() => {
    if (!isDragging) {
      setPositionShift({ x: 0, y: 0 });
      return;
    }

    if (!isBetsOpenStatus) {
      document.removeEventListener('pointermove', handlePointerMove);
      return;
    }

    document.addEventListener('pointermove', handlePointerMove, { passive: true });

    return (): void => {
      document.removeEventListener('pointermove', handlePointerMove);
    };
  }, [handlePointerMove, isBetsOpenStatus, isDragging]);

  const shouldBeHidden =
    !isBetsOpenStatus ||
    !dragBuffer ||
    dragBuffer.preliminary ||
    dragBuffer.value <= 0 ||
    !dragBuffer.elementData.width;

  return (
    <div
      className={styles.draggableChip}
      style={{
        left: dragBuffer
          ? `calc(${dragBuffer.elementData.origin.x - dragBuffer.elementData.width / 2}px - env(safe-area-inset-left))`
          : 0,
        opacity: shouldBeHidden ? 0 : 1,
        top: dragBuffer
          ? dragBuffer.elementData.origin.y -
            (isWide ? dragBuffer.elementData.width / 2 : dragBuffer.elementData.width * 2)
          : 0,
        width: dragBuffer ? dragBuffer.elementData.width * (isWide ? 1 : 1.25) : 0,
        transform: `translate(${positionShift.x}px, ${positionShift.y}px)`,
      }}
    >
      <ChipConnected size="grow" value={dragBuffer?.value || 0} />
    </div>
  );
};
